import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AlertCircle, CheckCircle, Camera, MapPin, LogIn } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/axios';
import './PublicBinReport.css';

const PublicBinReport = () => {
  const { binId } = useParams();
  const navigate = useNavigate();
  const [binData, setBinData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [userLocation, setUserLocation] = useState(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    // Check if user is authenticated
    const token = localStorage.getItem('binbuddy_token');
    setIsAuthenticated(!!token);
    
    // Fetch bin data
    fetchBinData();
  }, [binId]);

  const fetchBinData = async () => {
    try {
      setIsLoading(true);
      const response = await api.get(`/bins/public/${binId}`);
      setBinData(response.data.data);
    } catch (err) {
      console.error('Error fetching bin data:', err);
      setError('Bin not found or invalid QR code');
    } finally {
      setIsLoading(false);
    }
  };

  const getUserLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported'));
        return;
      }
      
      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
        },
        (error) => {
          reject(error);
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
      );
    });
  };

  const handleCameraOpen = () => {
    setCameraOpen(true);
    navigator.mediaDevices.getUserMedia({ 
      video: { 
        facingMode: 'environment',
        width: { ideal: 1280 },
        height: { ideal: 720 }
      } 
    })
      .then(stream => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      })
      .catch(() => {
        navigator.mediaDevices.getUserMedia({ video: true })
          .then(stream => {
            if (videoRef.current) {
              videoRef.current.srcObject = stream;
            }
          })
          .catch(err => {
            console.error("Error accessing camera: ", err);
            toast.error('Could not access camera. Please ensure you have granted permission.');
            setCameraOpen(false);
          });
      });
  };

  const handleCameraClose = () => {
    setCameraOpen(false);
    if (videoRef.current && videoRef.current.srcObject) {
      const stream = videoRef.current.srcObject;
      const tracks = stream.getTracks();
      tracks.forEach(track => track.stop());
    }
  };

  const handleCapture = () => {
    if (videoRef.current && canvasRef.current) {
      const context = canvasRef.current.getContext('2d');
      canvasRef.current.width = videoRef.current.videoWidth;
      canvasRef.current.height = videoRef.current.videoHeight;
      context.drawImage(videoRef.current, 0, 0, videoRef.current.videoWidth, videoRef.current.videoHeight);
      
      canvasRef.current.toBlob(blob => {
        const capturedFile = new File([blob], `bin-status-${Date.now()}.jpg`, { type: "image/jpeg" });
        setCapturedPhoto(capturedFile);
        setPhotoPreview(URL.createObjectURL(capturedFile));
        handleCameraClose();
        toast.success('Photo captured successfully!');
      }, 'image/jpeg', 0.8);
    }
  };

  const handleLogin = () => {
    // Store the current URL to redirect back after login
    localStorage.setItem('redirectAfterLogin', window.location.pathname);
    navigate('/signin');
  };

  const handleStatusUpdate = async () => {
    if (!isAuthenticated) {
      toast.error('Please login to report bin status');
      handleLogin();
      return;
    }

    if (!selectedStatus || !capturedPhoto) {
      toast.error('Please select a status and capture a photo');
      return;
    }

    setSubmitting(true);
    
    try {
      // Get user's current location
      const location = await getUserLocation();
      setUserLocation(location);

      // Create FormData
      const formData = new FormData();
      formData.append('binId', binData.binId);
      formData.append('status', selectedStatus.toLowerCase());
      formData.append('userLatitude', location.latitude);
      formData.append('userLongitude', location.longitude);
      formData.append('photo', capturedPhoto);

      // Get auth token
      const token = localStorage.getItem('binbuddy_token');
      
      // Submit status update
      await api.post(
        '/bins/update-status', 
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      toast.success('Bin status updated successfully! Thank you for helping keep our city clean.');
      
      // Reset form
      setSelectedStatus('');
      setCapturedPhoto(null);
      setPhotoPreview('');
      
      // Refresh bin data
      fetchBinData();
      
    } catch (err) {
      console.error('Error updating bin status:', err);
      if (err.response?.status === 401) {
        toast.error('Please login to report bin status');
        handleLogin();
      } else if (err.response?.data?.message) {
        toast.error(err.response.data.message);
      } else {
        toast.error('Failed to update bin status');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="public-report-container">
        <div className="loading-section">
          <div className="loader"></div>
          <p className="loading-text">Loading bin information...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="public-report-container">
        <div className="error-box">
          <AlertCircle className="icon" />
          <div>
            <p className="error-title">Bin Not Found</p>
            <p>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="public-report-container">
      <div className="header-section">
        <h1>🗑️ BinBuddy</h1>
        <p className="subtitle">Help keep our city clean by reporting bin status</p>
      </div>

      {binData && (
        <div className="bin-info-card">
          <div className="bin-header">
            <CheckCircle className="icon success" />
            <div>
              <h2>Bin Found!</h2>
              <p>Report the current status of this bin</p>
            </div>
          </div>

          <div className="bin-details">
            <div className="detail-item">
              <span className="label">Bin ID:</span>
              <span className="value">{binData.binId}</span>
            </div>
            <div className="detail-item">
              <span className="label">Current Status:</span>
              <span className={`status-badge ${binData.status.toLowerCase()}`}>
                {binData.status}
              </span>
            </div>
            <div className="detail-item">
              <span className="label">Capacity:</span>
              <span className="value">{binData.capacity}L</span>
            </div>
            <div className="detail-item">
              <span className="label">Location:</span>
              <span className="value">
                <MapPin className="location-icon" />
                {binData.address?.streetAddress || 'Unknown Location'}
              </span>
            </div>
          </div>

          {!isAuthenticated && (
            <div className="auth-notice">
              <LogIn className="icon" />
              <div>
                <p><strong>Login Required</strong></p>
                <p>Please login to report bin status and help maintain our city's cleanliness.</p>
                <button onClick={handleLogin} className="login-btn">
                  Login to Report
                </button>
              </div>
            </div>
          )}

          {isAuthenticated && (
            <div className="report-section">
              <h3>Update Bin Status</h3>
              
              <div className="status-selection">
                <h4>Current Condition</h4>
                <div className="status-buttons">
                  {['empty', 'partial', 'full', 'maintenance'].map(status => (
                    <button
                      key={status}
                      onClick={() => setSelectedStatus(status)}
                      className={`status-btn ${status} ${selectedStatus === status ? 'active' : ''}`}
                    >
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              <div className="photo-section">
                <h4>Capture Current Photo</h4>
                <p className="photo-desc">Take a photo to verify the bin's current condition</p>
                
                {photoPreview ? (
                  <div className="photo-preview">
                    <img src={photoPreview} alt="Captured bin" />
                    <button onClick={() => { setCapturedPhoto(null); setPhotoPreview(''); }} className="retake-btn">
                      Retake Photo
                    </button>
                  </div>
                ) : (
                  <button onClick={handleCameraOpen} className="camera-btn">
                    <Camera className="icon" />
                    Capture Photo
                  </button>
                )}
              </div>

              <div className="submit-section">
                <button 
                  onClick={handleStatusUpdate} 
                  disabled={!selectedStatus || !capturedPhoto || submitting}
                  className="submit-btn"
                >
                  {submitting ? 'Submitting Report...' : 'Submit Report'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Camera Modal */}
      {cameraOpen && (
        <div className="camera-modal">
          <div className="camera-modal-content">
            <div className="camera-header">
              <h3>Capture Bin Photo</h3>
              <button onClick={handleCameraClose} className="close-btn">×</button>
            </div>
            <div className="camera-body">
              <video ref={videoRef} autoPlay playsInline />
              <canvas ref={canvasRef} style={{ display: 'none' }} />
            </div>
            <div className="camera-actions">
              <button onClick={handleCapture} className="capture-btn">
                <Camera className="icon" />
                Capture
              </button>
              <button onClick={handleCameraClose} className="cancel-btn">Cancel</button>
            </div>
          </div>
        </div>
      )}

      <div className="footer-info">
        <p>🌱 Thank you for helping keep our environment clean!</p>
        <p>Your report helps city management maintain waste collection efficiently.</p>
      </div>
    </div>
  );
};

export default PublicBinReport;
